import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import { evict } from "shared/api/evict"
import { evictAffectedPrices } from "shared/api/price/evictPriceCache"
import { invalidateAffectedPrices } from "shared/api/price/modifyPriceCache"
import { MutationSchema, MutationTypename } from "shared/api/types"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import {
  BatchCreateableProductPosition,
  ProductPosition
} from "shared/types/ProductPosition"
import {
  ProductPositionTypename,
  ProjectParentTypename,
  ProjectTypename,
  isProjectParentTypename
} from "shared/types/Project"
import {
  BATCH_CREATE_FAVORITE,
  BATCH_CREATE_PRODUCT_POSITION
} from "shared/types/ProjectMutations"
import { ProductPositionSchema } from "shared/types/__generated__/validation"

import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { useBatchCreateFavoritesMutation } from "entities/favorites/graphql/mutation.generated"
import { useFavoritesLazyQuery } from "entities/favorites/graphql/queries.generated"
import {
  BatchCreateProductPositionsDocument,
  BatchCreateProductPositionsMutation,
  BatchCreateProductPositionsMutationVariables
} from "entities/product-positions/graphql/mutations.generated"
import {
  insertProjectChildren,
  insertProjectChildrenIntoParent
} from "entities/project-children/lib/insertProjectChildren"
import { useProjectLazyQuery } from "entities/projects/graphql/queries.generated"
import { useProjectStoreActions } from "features/project-management/model/store/useProjectStoreActions"
import z from "zod"

import { getProductPositionComplete } from "../lib/getProductPositionComplete"
import { BatchCreateProductPositionsVariablesSchema } from "../types/ProductPosition"

const BatchCreateProductPositionsResponseSchema = MutationSchema.extend({
  productPositions: z.array(ProductPositionSchema())
})

export function useBatchCreateProductPositions() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectLazyQuery()
  const [fetchFavorites] = useFavoritesLazyQuery()
  const { setElementPending, unsetElementPending } = useProjectStoreActions()

  const { raiseLoadingToast: raiseRegularLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.ProductPosition.BATCH_CREATE_PRODUCT_POSITION.Start"),
      success: t(
        "Request.ProductPosition.BATCH_CREATE_PRODUCT_POSITION.Success"
      ),
      failure: t(
        "Request.ProductPosition.BATCH_CREATE_PRODUCT_POSITION.Failure"
      )
    }
  })

  const { raiseLoadingToast: raiseFavoriteLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Favorite.BATCH_CREATE_FAVORITE.Start"),
      success: t("Request.Favorite.BATCH_CREATE_FAVORITE.Success"),
      failure: t("Request.Favorite.BATCH_CREATE_FAVORITE.Failure")
    }
  })

  const startProjectRequest = useProjectRequestMonitor()

  const [apolloMutate, rest] = useApolloMutation<
    BatchCreateProductPositionsMutation,
    BatchCreateProductPositionsMutationVariables
  >(BatchCreateProductPositionsDocument)

  const [mutateFavorite] = useBatchCreateFavoritesMutation({
    update: (cache, result) => {
      if (result.data?.productPositions) {
        insertProjectChildrenIntoParent({
          children: result.data.productPositions,
          parentID: FAVORITES_PROJECT_ID,
          parentType: ProjectTypename,
          cache
        })
      }
    }
  })

  const batchCreateProductPositions = useEvent(
    async ({
      productPositions,
      projectID
    }: {
      projectID: string
      productPositions: BatchCreateableProductPosition[]
    }) => {
      const completedProductPositions = productPositions.reduce(
        (acc, productPosition) => {
          const complete = getProductPositionComplete({
            productPosition: { ...productPosition, id: generateUUID() }
          })
          if (complete) {
            acc.push({ ...complete, parent: productPosition.parent })
          }
          return acc
        },
        [] as (ProductPosition & {
          parent: BatchCreateableProductPosition["parent"]
        })[]
      )

      const optimisticResponse = {
        __typename: MutationTypename,
        productPositions: completedProductPositions.map(productPosition => ({
          ...productPosition,
          price: productPosition.price ?? null,
          priceGroup: productPosition.priceGroup ?? null
        }))
      }

      const input = completedProductPositions.map(productPosition => ({
        ...productPosition,
        price: productPosition.price ?? undefined
      }))

      const onError = () => {
        input.forEach(data => {
          if (data.id) {
            evict({
              id: data.id,
              typename: ProductPositionTypename,
              cache: apolloCache
            })
          }
        })
      }

      const isFavorite = projectID === FAVORITES_PROJECT_ID

      if (isFavorite) {
        await fetchFavorites()

        input.forEach(productPosition => {
          if (productPosition.id) {
            setElementPending({
              itemID: productPosition.id,
              projectID
            })
          }
        })

        const { raiseSuccessToast, raiseErrorToast } =
          raiseFavoriteLoadingToast()
        const completeProjectRequest = startProjectRequest({
          projectID,
          requestType: BATCH_CREATE_FAVORITE
        })

        return mutateFavorite({
          variables: { input },
          optimisticResponse,
          onCompleted: () => {
            input.forEach(productPosition => {
              if (productPosition.id) {
                unsetElementPending({
                  itemID: productPosition.id,
                  projectID
                })
              }
            })

            completeProjectRequest()
            raiseSuccessToast()
          },
          onError: () => {
            onError()

            input.forEach(productPosition => {
              if (productPosition.id) {
                unsetElementPending({
                  itemID: productPosition.id,
                  projectID
                })
              }
            })

            completeProjectRequest()
            raiseErrorToast()
          }
        })
      }

      await fetchProject({ variables: { id: projectID } })

      completedProductPositions.forEach(productPosition => {
        if (productPosition.id) {
          setElementPending({
            itemID: productPosition.id,
            projectID
          })

          if (productPosition.parent?.id && productPosition.parent?.type) {
            invalidateAffectedPrices({
              projectID,
              itemID: productPosition.parent.id,
              itemType: productPosition.parent.type as ProjectParentTypename,
              includeChild: true,
              cache: apolloCache
            })
          }
        }
      })

      const { raiseSuccessToast, raiseErrorToast } = raiseRegularLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: BATCH_CREATE_PRODUCT_POSITION
      })

      return callMutation(apolloMutate, {
        variables: {
          projectID,
          input
        },
        transform: data => data.productPositions,
        schemas: {
          input: BatchCreateProductPositionsVariablesSchema,
          response: BatchCreateProductPositionsResponseSchema
        },
        apolloOptions: {
          optimisticResponse,
          update: (cache, result) => {
            if (result.data?.productPositions) {
              const resultWithParents = result.data.productPositions.reduce(
                (acc, productPosition) => {
                  const parent = input.find(
                    inputProductPosition =>
                      inputProductPosition.id === productPosition.id
                  )?.parent

                  if (parent && isProjectParentTypename(parent.type)) {
                    acc.push({
                      ...productPosition,
                      parent: { id: parent.id, type: parent.type }
                    })
                  }
                  return acc
                },
                [] as (ProductPosition & {
                  parent: { id: string; type: ProjectParentTypename }
                })[]
              )

              insertProjectChildren({
                children: resultWithParents,
                cache
              })
            }
          },
          onCompleted: (data: BatchCreateProductPositionsMutation) => {
            data.productPositions.forEach(productPosition => {
              unsetElementPending({
                itemID: productPosition.id,
                projectID
              })

              evictAffectedPrices({
                projectID,
                itemID: productPosition.id,
                itemType: ProductPositionTypename,
                cache: apolloCache
              })
            })

            completeProjectRequest()
            raiseSuccessToast()
          },
          onError: () => {
            onError()

            input.forEach(productPosition => {
              if (productPosition.id) {
                unsetElementPending({
                  itemID: productPosition.id,
                  projectID
                })
              }
            })

            completeProjectRequest()
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [batchCreateProductPositions, rest] as [
    typeof batchCreateProductPositions,
    typeof rest
  ]
}
